import React, { useState, useMemo } from "react";
import { Box, Typography, CircularProgress, Autocomplete, TextField } from "@mui/material";
import { useGetList } from "react-admin";
import { Client, ResourceName } from "@/types";
import { FileClientChoice } from "./vendorAssignmentShared";

// =============================================================================
// Client search field (#7551) — the review's ClientGate picker, as a field
// =============================================================================

interface ClientSearchFieldProps {
  value: FileClientChoice | null;
  onChange: (client: FileClientChoice | null) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  /** Field label rendered inside the outline (shrunk). Omitted → placeholder only. */
  label?: string;
  /** MUI size; the upload dialog uses medium, inline row pickers small. */
  size?: "small" | "medium";
}

/**
 * Server-side client search (same `q` filter and sort as the review's ClientGate,
 * #5697). Clients without an active policy stay visible and disabled, with the
 * reason, since claims and reimbursements need a policy to review against.
 */
export const ClientSearchField = ({
  value,
  onChange,
  disabled,
  autoFocus,
  placeholder = "Search clients…",
  ariaLabel = "Client",
  label,
  size = "small",
}: ClientSearchFieldProps) => {
  const [searchText, setSearchText] = useState("");
  const [open, setOpen] = useState(false);
  const { data: clients = [], isLoading } = useGetList<Client>(
    ResourceName.CLIENTS,
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "client_legal_name", order: "ASC" },
      filter: searchText ? { q: searchText } : {},
    },
    { enabled: open }
  );
  const selected = useMemo<Client | null>(
    () => (value ? ({ id: value.id, client_legal_name: value.name } as Client) : null),
    [value]
  );
  const options = useMemo(() => {
    if (selected && !clients.some((c) => String(c.id) === String(selected.id))) return [selected, ...clients];
    return clients;
  }, [clients, selected]);

  return (
    <Autocomplete
      size={size}
      fullWidth
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      filterOptions={(x) => x}
      getOptionLabel={(o) => o.client_legal_name || ""}
      getOptionDisabled={(o) => o.has_active_policy === false}
      isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
      value={selected}
      onChange={(_e, c) => onChange(c ? { id: String(c.id), name: c.client_legal_name } : null)}
      onInputChange={(_e, v, reason) => setSearchText(reason === "input" ? v : "")}
      loading={isLoading}
      disabled={disabled}
      noOptionsText={searchText ? "No matches found" : "Type to search…"}
      renderOption={(props, option) => {
        const { key, ...rest } = props as React.HTMLAttributes<HTMLLIElement> & { key?: React.Key };
        return (
          <li key={key} {...rest}>
            <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", gap: 1 }}>
              <Typography variant="body2">{option.client_legal_name}</Typography>
              {option.has_active_policy === false && (
                <Typography variant="caption" color="text.secondary">
                  No active policy
                </Typography>
              )}
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          InputLabelProps={label ? { shrink: true } : undefined}
          inputProps={{ ...params.inputProps, "aria-label": ariaLabel }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading && <CircularProgress color="inherit" size={18} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

/**
 * The vendor list the prepare step offers. One query shape, so a caller that
 * mounts before the first drop (the upload dialog's files step) warms the same
 * cache the panel reads, and the rows render their choices on first paint
 * instead of flickering from search field to radios once the list lands.
 */
